import React from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

export const useAccent = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  return { darkMode, accentName: darkMode ? "secondary" : "primary" };
};

export const ranuButtonSx = ({ palette }) => ({
  boxShadow: "none",
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  "&:hover": {
    backgroundColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

export const ranuOutlinedButtonSx = ({ palette }) => ({
  boxShadow: "none",
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  "&:hover": {
    backgroundColor: "transparent",
    borderColor: palette.secondary.main,
    color: palette.secondary.main,
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// Text buttons (Back to search): gold text on hover
export const ranuTextButtonSx = ({ palette }) => ({
  boxShadow: "none",
  "&:hover": { color: palette.secondary.main, boxShadow: "none" },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The dark theme has palette.background.card, the light theme uses white cards
export const cardBackground = ({ palette }) => palette.background.card || palette.white.main;

// Ranu gold, used for the focus border of every input inside a receipt card
const FOCUS_GOLD = "#C89B3C";

const SectionCard = ({ step, icon, title, subtitle, done, children }) => {
  const { darkMode, accentName } = useAccent();

  // The circle is only shown when there is something to put in it
  // (done tick, an icon or a step number). Without any of them there is no empty circle.
  const showCircle = Boolean(done || icon || step);

  return (
    <MDBox
      mb={3}
      p={{ xs: 2, md: 3 }}
      sx={(theme) => ({
        position: "relative",
        overflow: "hidden",
        backgroundColor: cardBackground(theme),
        border: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
        borderRadius: theme.borders.borderRadius.xl,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          backgroundColor: theme.palette[accentName].main,
        },

        // Focus look for ALL inputs in this card (search box, fields, selects):
        // gold border instead of the default blue, and no blue glow.
        // Fields in an error state keep their red border.
        "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
          borderColor: FOCUS_GOLD,
          borderWidth: "2px",
        },
        "& .MuiInputBase-root.Mui-focused": {
          boxShadow: "none",
          outline: "none",
        },
        "& .MuiInput-root:after": { borderBottomColor: FOCUS_GOLD },
      })}
    >
      <MDBox display="flex" alignItems="center" gap={1.5} mb={3}>
        {showCircle && (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={({ palette }) => ({
              width: 34,
              height: 34,
              minWidth: 34,
              borderRadius: "50%",
              border: `2px solid ${palette[accentName].main}`,
              backgroundColor: done ? palette[accentName].main : "transparent",
              color: done
                ? darkMode
                  ? palette.black.main
                  : palette.white.main
                : palette[accentName].main,
              fontWeight: 700,
              fontSize: 14,
            })}
          >
            {done ? (
              <Icon fontSize="small">check</Icon>
            ) : icon ? (
              <Icon fontSize="small">{icon}</Icon>
            ) : (
              step
            )}
          </MDBox>
        )}
        <MDBox>
          <MDTypography variant="h6" color={accentName}>
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography variant="caption" color="text" component="div">
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      {children}
    </MDBox>
  );
};

SectionCard.propTypes = {
  step: PropTypes.number,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  done: PropTypes.bool,
  children: PropTypes.node,
};

SectionCard.defaultProps = {
  step: null,
  icon: "",
  subtitle: "",
  done: false,
  children: null,
};

// Page header (badge + short description). Same look as ReceiptHeader, but reusable:
// pass the icon, the badge label and the description for any page.
export const SectionHeader = ({ icon, label, description, startAction, endAction }) => {
  const { darkMode, accentName } = useAccent();
  const hasActions = Boolean(startAction || endAction);

  const badge = (
    <MDBox
      display="inline-flex"
      alignItems="center"
      gap={1.25}
      mb={hasActions ? 0 : 1.5}
      sx={({ palette, borders }) => ({
        padding: "6px 16px 6px 6px",
        borderRadius: "50px",
        backgroundColor: darkMode ? "transparent" : palette.light.main,
        border: `${borders.borderWidth[1]} solid ${borders.borderColor}`,
      })}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={({ palette }) => ({
          width: 34,
          height: 34,
          borderRadius: "50%",
          color: darkMode ? palette.black.main : palette.white.main,
          background: `linear-gradient(135deg, ${palette[accentName].main}, ${palette[accentName].focus})`,
        })}
      >
        <Icon fontSize="small">{icon}</Icon>
      </MDBox>
      <MDTypography variant="button" fontWeight="bold" color={accentName}>
        {label}
      </MDTypography>
    </MDBox>
  );

  return (
    <MDBox mb={3} sx={{ textAlign: { xs: "center", md: "left" } }}>
      {hasActions ? (
        // optional buttons: startAction sits beside the badge, endAction is pushed to the right
        <MDBox
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={1.5}
          sx={{ justifyContent: { xs: "center", md: "flex-start" } }}
        >
          {startAction}
          {badge}
          {endAction && <MDBox sx={{ marginLeft: { xs: 0, md: "auto" } }}>{endAction}</MDBox>}
        </MDBox>
      ) : (
        badge
      )}
      {description && (
        <MDTypography variant="body2" color="text" sx={{ fontSize: "0.95rem" }}>
          {description}
        </MDTypography>
      )}
    </MDBox>
  );
};

SectionHeader.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  startAction: PropTypes.node,
  endAction: PropTypes.node,
};

SectionHeader.defaultProps = {
  description: "",
  startAction: null,
  endAction: null,
};

export default SectionCard;
